<template>
	<TaskListRowSkeleton
		v-if="!task.permissions.canView"
		:id="task.id"
		v-tooltip="$t('task_no_permissions_to_view')"
		:animate="false"
		:name="task.name"
		:show-subtasks="showSubtasks"
		:is-subtask="isSubtask"
		:col-definition="colDefinition"
		:is-open="isOpen"
		:is-subtasks-disabled="isSubtasksDisabled"
		:subtasks-count="subtasksCount"
		:expand-collapse-tooltip="expandCollapseTooltip"
		@expand-collapse="onExpandCollapse"
	/>
	<SuiDataTableRow
		v-else-if="task.__typename === TYPES.Task"
		class="task-list-row-task"
		:class="classAddons"
		@mouseover="onFocusIn()"
		@mouseleave="debouncedFocusOut()"
		@focusin="onFocusIn()"
		@focusout="debouncedFocusOut()"
	>
		<template v-for="cell in orderedCells" :key="cell.columnId">
			<SuiDataTableCell
				v-if="
					cell.columnId === TABLE_COLUMN.Collapsible && isParentTask
				"
				:class="getCellClass(cell.columnId)"
			>
				<TaskListRowExpandButton
					:is-open="isOpen"
					:is-disabled="isSubtasksDisabled"
					:expand-collapse-tooltip="expandCollapseTooltip"
					@click="onExpandCollapse(task.id)"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="
					cell.columnId === TABLE_COLUMN.Collapsible && isSubtask
				"
			/>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Assignee"
				:class="getCellClass(cell.columnId)"
				@click.self="onRowClick"
			>
				<TaskUsers
					size="medium"
					:task-id="task.id"
					:task-enrichment-data="taskEnrichmentData"
					:is-owner-visible="useTaskOwnerSetting"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Actions"
				:class="getCellClass(cell.columnId)"
				@click.self="onRowClick"
			>
				<TimeEntryActions
					v-if="task.permissions.canModify && timeEntryRequiredFields"
					class="task-list-row-task__time-entry"
					:show-buttons="isMouseOver || mq.largeMinus"
					:task-id="task.id"
					:allow-quick-add="allowQuickTimeEntry"
					:allow-start-tracker="allowStartTracker"
					:is-required-fields-missing="task.isRequiredFieldsMissing"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Title"
				:class="getCellClass(cell.columnId)"
				@click.self="onRowClick"
			>
				<div class="task-list-row-task__title-wrapper">
					<TaskPriority
						v-if="task.priority"
						:task-id="task.id"
						:priority="task.priority"
						:is-done="task.isDone"
						:is-required-fields-missing="
							task.isRequiredFieldsMissing
						"
						:is-clickable="task.permissions.canModify"
						@click="
							rootEmit('show-event-popup', {
								eventType: 'task',
								eventId: task.id,
							})
						"
					/>
					<a class="task-list-row-task__title" :href="taskHref">
						<strong>
							{{
								task.name ||
								task.activityType?.name ||
								$t('general.unspecified')
							}}
						</strong>
					</a>
				</div>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Subitems"
				:class="getCellClass(cell.columnId)"
			>
				<div
					v-if="isParentTask"
					class="task-list-row-task__subtask-actions"
				>
					<SubtaskCount
						v-if="subtasksCount"
						:count="subtasksCount"
						@click="onExpandCollapse(task.id)"
					/>
					<SuiButton
						v-else-if="!task.isDone"
						v-tooltip="$t('add_subtask')"
						class="task-list-row-task__add-subtask"
						variant="icon"
						:aria-label="$t('add_subtask')"
						@click="onExpand(task.id)"
					>
						<SuiIcon name="icon-task-new-16" />
					</SuiButton>
				</div>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Comments"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				@click.self="onRowClick"
			>
				<CommentCount
					:count="task.commentsCount"
					:href="`${taskHref}#commentsTable`"
					:is-done="task.isDone"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Status"
				:class="getCellClass(cell.columnId)"
				@click.self="onRowClick"
			>
				<StatusPicker
					v-if="taskStatus && task.id"
					:module="StatusModulesEnum.Tasks"
					:object-id="task.id"
					:status="taskStatus"
					:is-disabled="!task.permissions.canModify || task.isDone"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.ActivityType"
				:class="getCellClass(cell.columnId)"
				@click.self="onRowClick"
			>
				<ActivityTypeInlineSelectButton
					v-if="task.permissions.canModify"
					:activity-type="task.activityType"
					:is-done="task.isDone"
					@select="onActivityTypeSelect"
				/>
				<SuiRelatedContent
					v-else-if="task.activityType"
					icon="icon-bolt-16"
					:type="task.isDone ? 'secondary' : 'primary'"
				>
					{{ task.activityType?.name }}
				</SuiRelatedContent>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Progress"
				:class="getCellClass(cell.columnId)"
				@click.self="onRowClick"
			>
				<TaskProgress
					:duration-planned="task.totalDurationPlanned"
					:duration-done="task.totalDurationDone"
					:duration-remaining="task.totalDurationRemaining"
					:duration-scheduled="task.totalDurationScheduled"
					:progress-percentage="task.totalProgressPercentage"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.StartDate"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				:is-number="cell.isNumber"
			>
				<TaskListCellStartDate
					v-if="
						task.permissions.canModify &&
						task.__typename === TYPES.Task
					"
					:task-id="task.id"
					:start-date="task.startDate"
					:use-deadline-with-time="useDeadlineWithTimeSetting"
					:due-date="task.dueDate"
					:due-date-time="task.dueDateTime"
					@task-updated="emit('task-updated', $event)"
				/>
				<span
					v-else-if="task.__typename === TYPES.Task"
					class="task-list-row-task__start-date-readonly"
				>
					{{ formattedStartDateReadonly }}
				</span>
				<span v-else class="task-list-row-task__start-date-readonly" />
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.DueDate"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				:is-number="cell.isNumber"
			>
				<TaskListCellDueDate
					v-if="task.permissions.canModify"
					:task-id="task.id"
					:is-done="task.isDone"
					:due-date-time="
						useDeadlineWithTimeSetting
							? task.dueDateTime
							: task.dueDate
					"
					:show-time="useDeadlineWithTimeSetting"
					:start-date="task.startDate"
					@task-updated="emit('task-updated', $event)"
				/>
				<TaskDueDate
					v-else
					:is-done="task.isDone"
					:due-date-time="
						useDeadlineWithTimeSetting
							? task.dueDateTime
							: task.dueDate
					"
					:show-time="useDeadlineWithTimeSetting"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Budget"
				v-tooltip="
					task.isQuoteLineShared
						? $t('task_list_budget_is_shared')
						: $t('task_list_budget_column_tooltip')
				"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				:is-number="cell.isNumber"
				@click.self="onRowClick"
			>
				{{ formattedBudgetDuration }}
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.RemainingBudget"
				v-tooltip="
					task.isQuoteLineShared
						? $t('task_list_remaining_budget_is_shared')
						: $t('task_list_remaining_budget_column_tooltip')
				"
				:class="[
					getCellClass(cell.columnId),
					{
						'task-list-row-task__cell--negative':
							isRemainingBudgetNegative,
					},
				]"
				:align="cell.align"
				:is-number="cell.isNumber"
				@click.self="onRowClick"
			>
				{{ formattedRemainingBudget }}
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Planned"
				v-tooltip="includesSubtasksTooltip"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				:is-number="cell.isNumber"
				@click.self="onRowClick"
			>
				<TaskListCellPlannedTime
					v-if="
						task.permissions.canModify &&
						task.__typename === TYPES.Task &&
						!subtasksCount
					"
					:task-id="task.id"
					:total-duration-planned="task.totalDurationPlanned"
					@task-updated="emit('task-updated', $event)"
				/>
				<span v-else class="task-list-row-task__planned-readonly">
					{{ formatDuration(task.totalDurationPlanned) }}
				</span>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Done"
				v-tooltip="includesSubtasksTooltip"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				:is-number="cell.isNumber"
				@click.self="onRowClick"
			>
				{{ formatDuration(task.totalDurationDone) }}
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Remaining"
				v-tooltip="includesSubtasksTooltip"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				:is-number="cell.isNumber"
				@click.self="onRowClick"
			>
				{{ formatDuration(task.totalDurationRemaining) }}
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.MarkDone"
				:class="getCellClass(cell.columnId)"
				:align="cell.align"
				@click.self="onRowClick"
			>
				<TimeEntryDone
					v-if="
						task.permissions.canModify &&
						!isCUPOUser &&
						timeEntryRequiredFields
					"
					:is-done="showTaskAsDone"
					:task-id="task.id"
					:allow-change-duration="
						timeEntryRequiredFields.length === 0
					"
					:has-uncompleted-subtasks="hasUncompletedSubtasks"
					@done="onMarkTaskDone"
				/>
			</SuiDataTableCell>
		</template>
	</SuiDataTableRow>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, nextTick } from 'vue';
import {
	useFormatter,
	useMfeUtils,
	usePermissions,
	useTranslation,
} from '@/composables';
import { Constants } from '@/constants';
import {
	type TaskListTaskFragment,
	StatusModulesEnum,
	useTaskListTaskSaveMutation,
	useTaskListTaskMarkAsDoneMutation,
	useTaskListTaskMarkAsNotDoneMutation,
	type StatusPickerStatusFragment,
	type Task,
	type TaskListTaskFieldsFragment,
	type ActivityTypeInlineSelectFragment,
	type TaskListEnrichmentFieldsFragment,
	TYPES,
	type LimitedTaskFieldsFragment,
} from '@/generated/graphql';
import {
	SuiDataTableRow,
	SuiDataTableCell,
	SuiMessageTypeType,
	SuiRelatedContent,
	SuiIcon,
	SuiButton,
	type SuiDataTableColumnType,
} from '@developers/scoro-ui';
import {
	TaskListCellDueDate,
	TaskListCellPlannedTime,
	TaskListCellStartDate,
} from '../TaskListCell';
import { TaskPriority } from '../../TaskPriority';
import { TaskProgress } from '../../TaskProgress';
import { StatusPicker } from '@/features/Status';
import { CommentCount } from '@/features/Comment';
import {
	TimeEntryActions,
	TimeEntryDone,
	TimeEntryRequiredFields,
} from '@/features/TimeEntry';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';
import { type ShowEventPopupPayload } from '@/composables/useMfeUtils';
import debounce from 'lodash/debounce';
import { useRequiredFields } from '@/composables/useRequiredFiels';
import { Module } from '@/constants/modules';
import { useMessage } from '@/features/Message';
import { useMq } from 'vue3-mq';
import { useCurrentUser } from '@/features/Auth';
import TaskDueDate from '@/features/Task/components/TaskDueDate/TaskDueDate.vue';
import TaskUsers from '@/features/Task/components/TaskUsers/TaskUsers.vue';
import { SubtaskCount } from '@/features/Subtask';
import { useLocalStorage } from '@vueuse/core';
import { LocalStorageKeys } from '@/constants/storage';
import { ActivityTypeInlineSelectButton } from '@/features/ActivityType';
import { useTaskListStore } from '../store';
import TaskListRowSkeleton from './TaskListRowSkeleton.vue';
import TaskListRowExpandButton from './TaskListRowExpandButton.vue';
import { TABLE_COLUMN } from '../constants';
import { useTaskListRowCells } from './composables';
import { Feature } from '@/constants/features';
import { type GraphQLFormattedError } from 'graphql';
import { resetSapiDateString } from '@/utils/formatting';

export type TaskListRowTaskProps = {
	task: TaskListTaskFieldsFragment | LimitedTaskFieldsFragment;
	colDefinition?: SuiDataTableColumnType[];
	subtasksCount?: number;
	showSubtasks?: boolean;
	isSubtask?: boolean;
	hasUncompletedSubtasks?: boolean;
	taskEnrichmentData?: TaskListEnrichmentFieldsFragment | null;
};

const props = defineProps<TaskListRowTaskProps>();
const emit = defineEmits<{
	(e: 'show-event-popup', payload: ShowEventPopupPayload): void;
	(e: 'task-updated', task?: TaskListTaskFragment): void;
}>();

const { task, subtasksCount } = toRefs(props);

const { rootEmit } = useMfeUtils();
const mq = useMq();
const { userHas } = usePermissions();
const { shortDuration, formatDateNoCurrentYear } = useFormatter();
const { requiredFields: timeEntryRequiredFields } = useRequiredFields(
	Module.TIME_ENTRIES.name
);
const [useDeadlineWithTimeSetting] = useBooleanSetting(
	Setting.USE_DEADLINE_WITH_TIME
);
const [useTaskOwnerSetting] = useBooleanSetting(Setting.USE_TASK_OWNER);
const openTaskIds = useLocalStorage(
	LocalStorageKeys.SubtaskListOpenTasks,
	[] as Array<Task['id']>
);

const { mutate: taskSave } = useTaskListTaskSaveMutation({
	errorPolicy: 'all',
	context: {
		handleErrorLocally: true,
	},
});
const { mutate: markTaskDone } = useTaskListTaskMarkAsDoneMutation({
	errorPolicy: 'all',
	context: {
		handleErrorLocally: true,
	},
});
const { mutate: markTaskNotDone } = useTaskListTaskMarkAsNotDoneMutation({
	errorPolicy: 'all',
	context: {
		handleErrorLocally: true,
	},
});
const { t } = useTranslation();
const { addMessage, addErrorMessage, removeMessage, DEFAULT_TIMEOUT_SUCCESS } =
	useMessage();
const { isCUPOUser } = useCurrentUser();
const taskListStore = useTaskListStore();

const isTaskOptimisticallyDone = ref(false);
const isMouseOver = ref(false);

const formatDuration = (duration: number, showZero = false) => {
	if (!duration && !showZero) {
		return '';
	}

	return shortDuration(duration)[0].value;
};

const taskHref = `/${Constants.Module.TASKS.name}/${Constants.Action.VIEW}/${props.task.id}`;

const onFocusOut = () => {
	isMouseOver.value = false;
};

const onRowClick = () => {
	window.location.href = taskHref;
};

const debouncedFocusOut = debounce(onFocusOut, 10);

const onFocusIn = () => {
	isMouseOver.value = true;
	debouncedFocusOut.cancel();
};

const errorMessage = (error: GraphQLFormattedError) => {
	return typeof error.extensions?.translation === 'string'
		? t(error.extensions.translation, error.extensions?.translation_fields)
		: error.message;
};

const onActivityTypeSelect = async (
	activityType: ActivityTypeInlineSelectFragment
) => {
	// Don't save if it's the same activity type
	if (
		task.value.__typename === TYPES.Task &&
		activityType.id === task.value.activityType?.id
	) {
		return;
	}

	const result = await taskSave({
		input: {
			taskId: props.task.id,
			activityTypeId: activityType.id,
		},
	});

	if (result?.data?.TaskSave) {
		emit('task-updated', result?.data?.TaskSave);
	}

	if (result?.errors) {
		result?.errors.forEach((error) => {
			addErrorMessage({
				message: errorMessage(error),
			});
		});
	}
};

const onMarkTaskNotDone = async () => {
	isTaskOptimisticallyDone.value = false;

	const result = await markTaskNotDone({
		input: {
			taskId: props.task.id,
		},
	});

	if (result?.errors) {
		isTaskOptimisticallyDone.value = true;

		result?.errors.forEach((error) => {
			addErrorMessage({
				message: errorMessage(error),
			});
		});
		return;
	}

	emit('task-updated', result?.data?.TaskMarkAsNotDone);
};

const onMarkTaskDone = async (duration: number) => {
	isTaskOptimisticallyDone.value = true;

	const result = await markTaskDone({
		input: {
			taskId: props.task.id,
			duration,
		},
	});

	if (result?.data?.TaskMarkAsDone) {
		const messageId = `task_marked_as_done_message-${task.value.id}`;
		addMessage({
			id: messageId,
			message: t('task_marked_as_done_message', {
				name: `<a href="${taskHref}">${task.value.name}</a>`,
			}),
			type: SuiMessageTypeType.Success,
			timeout: DEFAULT_TIMEOUT_SUCCESS,
			showUndo: true,
			undoLabel: t('undo'),
			onUndo() {
				onMarkTaskNotDone();
				removeMessage(messageId);
			},
		});
	}

	if (result?.errors) {
		isTaskOptimisticallyDone.value = false;

		result?.errors.forEach((error) => {
			addErrorMessage({
				message: errorMessage(error),
			});
		});
		return;
	}

	emit('task-updated', result?.data?.TaskMarkAsDone);
};

const showTaskAsDone = computed(
	() =>
		(task.value.__typename === TYPES.Task && task.value.isDone) ||
		isTaskOptimisticallyDone.value
);

const taskStatus = computed<StatusPickerStatusFragment>(() => {
	if (task.value.__typename === TYPES.Task && task.value.status) {
		return task.value.status;
	}

	return {
		color: '#f0f3fa',
		id: '-',
		name: t('assignStatus'),
		statusId: t('assignStatus'),
		__typename: 'Status',
	};
});

const allowQuickTimeEntry = computed(() => {
	if (task.value.__typename !== TYPES.Task) {
		return false;
	}

	if (
		timeEntryRequiredFields.value?.length === 1 &&
		timeEntryRequiredFields.value[0] === TimeEntryRequiredFields.Activity &&
		task.value.activityType
	) {
		return true;
	}

	return timeEntryRequiredFields.value?.length === 0;
});

const onExpand = async (taskId: Task['id']) => {
	openTaskIds.value = [...(openTaskIds.value ?? []), taskId];
	await nextTick();
	taskListStore.editModeTaskId = taskId;
};

const onExpandCollapse = async (taskId: Task['id']) => {
	const isOpen = openTaskIds.value.some((id) => id === task.value.id);

	if (isOpen) {
		openTaskIds.value = openTaskIds.value.filter((id) => id !== taskId);
	} else {
		openTaskIds.value = [...(openTaskIds.value ?? []), taskId];

		await nextTick();
		taskListStore.editModeTaskId = taskId;
	}
};

const isOpen = computed(() => {
	return openTaskIds.value.some((id) => id === task.value.id);
});

const isSubtasksDisabled = computed(() => {
	return (
		task.value.__typename === TYPES.Task &&
		!subtasksCount.value &&
		task.value.isDone
	);
});

const expandCollapseTooltip = computed(() => {
	if (isSubtasksDisabled.value) {
		return undefined;
	}

	return isOpen.value ? t('collapse') : t('expand');
});

const isExpanded = computed(() => {
	if (task.value.__typename !== TYPES.Task) {
		return false;
	}

	if (task.value.isDone && !subtasksCount.value) {
		return false;
	}

	return isOpen.value && !props.isSubtask && props.showSubtasks;
});

const isParentTask = computed(() => props.showSubtasks && !props.isSubtask);

const colDefinitionRef = computed(() => props.colDefinition || []);

const { orderedCells } = useTaskListRowCells({
	colDefinition: colDefinitionRef,
});

const formattedStartDateReadonly = computed(() => {
	if (task.value.__typename !== TYPES.Task || !task.value.startDate) {
		return '';
	}

	return formatDateNoCurrentYear(resetSapiDateString(task.value.startDate));
});

const getCellClass = (columnId: string): string => {
	const baseClass = 'task-list-row-task__cell';
	const modifiers: string[] = [];

	if (columnId === TABLE_COLUMN.Collapsible) {
		modifiers.push('expand-collapse');
	} else if (columnId === TABLE_COLUMN.Assignee) {
		modifiers.push('assignees', 'clickable');
	} else if (columnId === TABLE_COLUMN.Actions) {
		modifiers.push('time-actions', 'clickable');
	} else if (columnId === TABLE_COLUMN.Title) {
		modifiers.push('name', 'clickable');
	} else if (columnId === TABLE_COLUMN.StartDate) {
		modifiers.push('start-date', 'clickable');
	} else if (columnId === TABLE_COLUMN.DueDate) {
		modifiers.push('due-date', 'clickable');
	} else if (columnId === TABLE_COLUMN.ActivityType) {
		modifiers.push('activity-type', 'clickable');
	} else if (columnId === TABLE_COLUMN.Planned) {
		modifiers.push('planned', 'clickable');
	} else if (columnId === TABLE_COLUMN.MarkDone) {
		modifiers.push('mark-done', 'clickable');
	} else if (
		columnId !== TABLE_COLUMN.Collapsible &&
		columnId !== TABLE_COLUMN.Subitems
	) {
		modifiers.push('clickable');
	}

	if (
		[
			TABLE_COLUMN.Planned,
			TABLE_COLUMN.Done,
			TABLE_COLUMN.Remaining,
		].includes(columnId)
	) {
		modifiers.push('summary');
	}

	return modifiers.length > 0
		? `${baseClass} ${modifiers.map((m) => `${baseClass}--${m}`).join(' ')}`
		: baseClass;
};

const includesSubtasksTooltip = computed(() => {
	return subtasksCount.value
		? t('task_list_includes_subtasks_tooltip')
		: null;
});

const budgetDuration = computed(() => {
	if (task.value.__typename !== TYPES.Task) {
		return null;
	}
	return task.value.budgetedDuration ?? null;
});

const remainingBudget = computed(() => {
	if (task.value.__typename !== TYPES.Task) {
		return null;
	}
	return task.value.remainingBudgetedDuration ?? null;
});

const isRemainingBudgetNegative = computed(() => {
	return remainingBudget.value !== null && remainingBudget.value < 0;
});

const formattedBudgetDuration = computed(() => {
	if (budgetDuration.value === null || !isParentTask.value) {
		return '';
	}

	const postfix =
		task.value.__typename === TYPES.Task && task.value.isQuoteLineShared
			? '*'
			: '';

	return `${formatDuration(budgetDuration.value, true)}${postfix}`;
});

const formattedRemainingBudget = computed(() => {
	if (!isParentTask.value || remainingBudget.value === null) {
		return '';
	}

	const postfix =
		task.value.__typename === TYPES.Task && task.value.isQuoteLineShared
			? '*'
			: '';

	return `${formatDuration(remainingBudget.value, true)}${postfix}`;
});

const classAddons = computed(() => {
	return {
		'task-list-row-task--is-done':
			task.value.__typename === TYPES.Task && task.value.isDone,
		'task-list-row-task--subtask': props.isSubtask,
		'task-list-row-task--expanded': isExpanded.value,
		'task-list-row-task--has-subtasks':
			subtasksCount.value && subtasksCount.value > 0,
	};
});

const allowStartTracker = computed(() => {
	return !task.value.isDone && userHas(Feature.TIME_TRACKER);
});
</script>

<style lang="scss">
.task-list-row-task {
	--sui-data-table-hover-bgc: #{$neutral-10};

	position: relative;

	.task-list-row-task__cell {
		&--expand-collapse {
			--sui-data-table-cell-padding: 0;
			--sui-data-table-side-spacing: 0;
		}

		&--assignees {
			--sui-data-table-cell-padding: 1.5rem 1rem;
		}

		&--name {
			padding-left: 1rem;
		}

		&--time-actions {
			--sui-data-table-cell-padding: 1.5rem 1rem;
		}

		&--start-date,
		&--due-date,
		&--planned {
			line-height: 1.75rem;
		}

		&--mark-done {
			--sui-data-table-cell-padding: 1rem;
		}

		&--clickable {
			cursor: pointer;
		}

		&--negative {
			color: $red-50;
			font-weight: $scoro-fw-bold;
		}
	}

	&__start-date-readonly,
	&__planned-readonly {
		display: block;
		text-align: right;
		font-feature-settings: 'tnum';
	}

	&__title-wrapper {
		width: 100%;
		font-size: 1.625rem;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		min-width: 30rem;
	}

	&__title {
		flex: 1;
	}

	&__time-entry {
		min-width: 10rem;
	}

	&__subtask-actions {
		display: flex;
		justify-content: center;
	}

	&__add-subtask {
		--sui-button-icon-color: #{$neutral-70};
		--sui-button-background-color: transparent;
		--sui-button-hover-background-color: #{$neutral-20};

		visibility: hidden;
	}

	&--is-done {
		--sui-icon-fill: #{$neutral-50};

		color: $neutral-70;

		.task-list-row-task__title {
			color: $neutral-70;
		}
	}

	&--subtask {
		--sui-data-table-cell-padding: 1rem 1.5rem;
		--sui-data-table-cell-border-bottom: 0;

		.task-list-row-task__cell--name,
		.task-list-row-task__cell--assignees {
			padding-left: 3rem;
		}

		.task-list-row-task__title-wrapper {
			strong {
				font-weight: $scoro-fw-normal;
			}
		}

		td:first-child {
			position: relative;

			&::before {
				content: ' ';
				position: absolute;
				width: 1px;
				top: 0;
				left: calc(50% - 2px);
				bottom: 0;
				background-color: $neutral-30;
			}

			&::after {
				content: ' ';
				position: absolute;
				height: 1px;
				top: 50%;
				right: 0;
				left: calc(50% - 2px);
				background-color: $neutral-30;
			}
		}

		&:has(+ .task-list-row-add-task--subtask) {
			td:first-child {
				&::before {
					bottom: 50%;
				}
			}
		}
	}

	&--expanded {
		--sui-data-table-cell-border-bottom: 0;

		z-index: 2;
		box-shadow: 0 8px 8px -4px rgb(73 79 89 / 9%);
	}

	&--has-subtasks {
		.task-list-row-expand-button {
			visibility: visible;
		}
	}

	&--expanded.task-list-row-task--has-subtasks
		.task-list-row-task__cell--summary {
		font-weight: $scoro-fw-bold;
	}

	&:hover {
		.task-list-row-task__title {
			color: $brand-50;
		}

		.task-list-row-expand-button {
			visibility: visible;
		}

		.task-list-row-task__add-subtask {
			visibility: visible;
		}
	}
}
</style>
